import { useCallback, useEffect, useState } from 'react';

import {
  deleteSshKeyApi,
  listSshKeysApi,
} from '../../../utilities/api/getDataFromApi';
import { KEY_SLOTS, MAX_KEYS } from '../constants';
import { deleteFailureBanner, deleteSuccessBanner } from '../helpers';

/**
 * Owns the Access Keys list: fetching the current keys and running the
 * delete-with-confirmation flow. Keeps AccessKeysSection focused on layout.
 *
 * @param {Object} params
 * @param {Function} params.setAlertBannerData - appends alert banners
 * @param {Function} params.localize - the localize function from useLocalize
 * @returns {Object} keys, loading/delete state, derived slot info, and handlers
 */
const useAccessKeys = ({ setAlertBannerData, localize }) => {
  const [accessKeys, setAccessKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const occupiedSlots = new Set(accessKeys.map((k) => k.slot));
  const nextAvailableSlot = KEY_SLOTS.find((s) => !occupiedSlots.has(s));
  const atKeyLimit = accessKeys.length >= MAX_KEYS;

  const fetchKeys = useCallback(() => {
    setIsLoading(true);
    listSshKeysApi()
      .then((response) => {
        if (Array.isArray(response)) {
          setAccessKeys(response);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleDeleteClick = useCallback((key) => {
    setKeyToDelete(key);
    setDeleteConfirmOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setKeyToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (keyToDelete === null) return;

    setIsDeleting(true);

    deleteSshKeyApi(keyToDelete.slot)
      .then((response) => {
        const isSuccess = response.status === 200 || response.status === 204;

        if (isSuccess) {
          fetchKeys();
        }

        setAlertBannerData((prev) => [
          ...prev,
          isSuccess
            ? deleteSuccessBanner(localize)
            : deleteFailureBanner(localize),
        ]);
      })
      .catch(() => {
        setAlertBannerData((prev) => [...prev, deleteFailureBanner(localize)]);
      })
      .finally(() => {
        setIsDeleting(false);
        setDeleteConfirmOpen(false);
        setKeyToDelete(null);
      });
  }, [keyToDelete, localize, fetchKeys, setAlertBannerData]);

  return {
    accessKeys,
    isLoading,
    nextAvailableSlot,
    atKeyLimit,
    fetchKeys,
    deleteConfirmOpen,
    isDeleting,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useAccessKeys;
